import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, MousePointerClick, Users, TrendingUp, TrendingDown,
  BarChart2, Eye, RefreshCw, Calendar, ChevronDown,
  AlertTriangle, XCircle, ShieldAlert, Clock, RotateCcw, CheckCircle2, Send, X, Search
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';

import { supabase } from '../../lib/supabase';



const fmt = (n) => n?.toLocaleString() ?? '—';
const pct = (a, b) => (b > 0 ? ((a / b) * 100)?.toFixed(1) : '0.0');

const MetricCard = ({ icon: IconComponent, label, value, sub, color, trend }) => {
  const colors = {
    teal: 'bg-teal-50 text-teal-600 border-teal-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    violet: 'bg-violet-50 text-violet-600 border-violet-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colors?.[color] || colors?.teal}`}>
          {IconComponent && <IconComponent className="w-5 h-5" />}
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-1 text-xs font-semibold ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm font-medium text-gray-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-2">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color }} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p?.color }} />
          {p?.name}: <strong>{p?.value}{typeof p?.value === 'number' && p?.name?.includes('%') ? '%' : ''}</strong>
        </p>
      ))}
    </div>
  );
};

// ── Metric Drilldown Modal ─────────────────────────────────────────────────
const MetricDrilldownModal = ({ isOpen, onClose, metricKey, metricLabel, campaignId, campaignSubject }) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const [resendMessage, setResendMessage] = useState('');

  const statusMap = {
    delivered: 'delivered',
    bounced: ['hard_fail', 'soft_fail', 'isp_rejection', 'bounced'],
    hardFails: 'hard_fail',
    softFails: 'soft_fail',
    ispRejections: 'isp_rejection',
    retryQueue: 'soft_fail',
  };

  const colorMap = {
    delivered: { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
    hard_fail: { badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
    soft_fail: { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    isp_rejection: { badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
    bounced: { badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
  };

  const labelMap = {
    delivered: 'Delivered',
    hard_fail: 'Hard Fail',
    soft_fail: 'Soft Fail',
    isp_rejection: 'ISP Rejection',
    bounced: 'Bounced',
  };

  useEffect(() => {
    if (!isOpen || !campaignId || !metricKey) return;
    setSelectedIds(new Set());
    setResendState('idle');
    setResendMessage('');

    const fetchRecipients = async () => {
      setLoading(true);
      setError(null);
      setSearch('');
      try {
        const { supabase } = await import('../../lib/supabase');
        const statusFilter = statusMap?.[metricKey];
        let query = supabase?.from('email_campaign_recipients')?.select('id, email, first_name, last_name, status, error_message, sent_at')?.eq('campaign_id', campaignId)?.order('sent_at', { ascending: false });

        if (Array.isArray(statusFilter)) {
          query = query?.in('status', statusFilter);
        } else {
          query = query?.eq('status', statusFilter);
        }

        const { data, error: fetchError } = await query;
        if (fetchError) throw fetchError;
        setRecipients(data || []);
      } catch (err) {
        setError(err?.message || 'Failed to load recipient data');
      } finally {
        setLoading(false);
      }
    };

    fetchRecipients();
  }, [isOpen, campaignId, metricKey]);

  const filtered = useMemo(() => {
    if (!search?.trim()) return recipients;
    const q = search?.toLowerCase();
    return recipients?.filter(r =>
      r?.email?.toLowerCase()?.includes(q) ||
      r?.first_name?.toLowerCase()?.includes(q) ||
      r?.last_name?.toLowerCase()?.includes(q)
    );
  }, [recipients, search]);

  const allFilteredSelected = filtered?.length > 0 && filtered?.every(r => selectedIds?.has(r?.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered?.forEach(r => next?.delete(r?.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered?.forEach(r => next?.add(r?.id));
        return next;
      });
    }
  };

  const selectedEmails = useMemo(() => {
    return recipients?.filter(r => selectedIds?.has(r?.id))?.map(r => r?.email);
  }, [recipients, selectedIds]);

  const handleResend = async () => {
    if (selectedEmails?.length === 0) return;
    setResendState('sending');
    setResendMessage('');
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: sessionData } = await supabase?.auth?.getSession();
      const token = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-reengagement-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject: campaignSubject || '👋 We miss you! Sign back in to MyBodyToday',
          target_emails: selectedEmails,
        }),
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Resend failed');

      setResendState('success');
      setResendMessage(`Campaign resent to ${selectedEmails?.length} recipient${selectedEmails?.length !== 1 ? 's' : ''} successfully.`);
      setSelectedIds(new Set());
    } catch (err) {
      setResendState('error');
      setResendMessage(err?.message || 'Failed to resend campaign');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-gray-900 truncate">{metricLabel} — Affected Emails</h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate">{campaignSubject}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!loading && (
              <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                {recipients?.length} recipient{recipients?.length !== 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Search + Select All */}
        {!loading && recipients?.length > 0 && (
          <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email or name…"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
              />
            </div>
            {filtered?.length > 0 && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-400 cursor-pointer"
                  />
                  <span className="text-xs text-gray-500 font-medium">
                    {allFilteredSelected ? 'Deselect all' : `Select all ${filtered?.length}`}
                  </span>
                </label>
                {selectedIds?.size > 0 && (
                  <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">
                    {selectedIds?.size} selected
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <RefreshCw className="w-6 h-6 text-teal-500 animate-spin" />
              <p className="text-sm text-gray-500">Loading recipients…</p>
            </div>
          )}

          {!loading && error && (
            <div className="mx-5 my-4 bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-700">Failed to load recipients</p>
                <p className="text-xs text-rose-500 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && recipients?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                <Mail className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600">No recipient data available</p>
              <p className="text-xs text-gray-400 text-center max-w-xs px-4">
                Recipient-level tracking is available for campaigns sent after this feature was enabled.
              </p>
            </div>
          )}

          {!loading && !error && filtered?.length === 0 && recipients?.length > 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <p className="text-sm text-gray-500">No results for "{search}"</p>
            </div>
          )}

          {!loading && !error && filtered?.length > 0 && (
            <div className="divide-y divide-gray-100">
              {filtered?.map((r) => {
                const colors = colorMap?.[r?.status] || colorMap?.delivered;
                const statusLabel = labelMap?.[r?.status] || r?.status;
                const name = [r?.first_name, r?.last_name]?.filter(Boolean)?.join(' ') || null;
                const isSelected = selectedIds?.has(r?.id);
                return (
                  <div
                    key={r?.id}
                    onClick={() => toggleSelect(r?.id)}
                    className={`px-5 py-3.5 flex items-start gap-3 cursor-pointer transition-colors ${isSelected ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(r?.id)}
                      onClick={e => e?.stopPropagation()}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-400 cursor-pointer flex-shrink-0"
                    />
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors?.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{r?.email}</p>
                      {name && <p className="text-xs text-gray-400 mt-0.5">{name}</p>}
                      {r?.error_message && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate" title={r?.error_message}>
                          {r?.error_message?.length > 80 ? r?.error_message?.slice(0, 80) + '…' : r?.error_message}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors?.badge}`}>
                        {statusLabel}
                      </span>
                      {r?.sent_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(r?.sent_at)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-2xl space-y-2">
          {/* Resend feedback */}
          {resendState === 'success' && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <p className="text-xs text-emerald-700 font-medium">{resendMessage}</p>
            </div>
          )}
          {resendState === 'error' && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
              <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />
              <p className="text-xs text-rose-700 font-medium">{resendMessage}</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              Showing {filtered?.length} of {recipients?.length} recipient{recipients?.length !== 1 ? 's' : ''}
            </p>
            {selectedIds?.size > 0 && (
              <button
                onClick={handleResend}
                disabled={resendState === 'sending'}
                className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors"
              >
                {resendState === 'sending' ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Resend to {selectedIds?.size} selected
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Delivery Status Breakdown Component ────────────────────────────────────
const DeliveryStatusBreakdown = ({ campaigns }) => {
  const [activeRetry, setActiveRetry] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { metricKey, metricLabel, campaignId, campaignSubject }

  const aggregate = useMemo(() => campaigns?.reduce((acc, c) => ({
    totalSent: acc?.totalSent + (c?.totalSent || 0),
    delivered: acc?.delivered + (c?.delivered || 0),
    bounced: acc?.bounced + (c?.bounced || 0),
    hardFails: acc?.hardFails + (c?.hardFails || 0),
    softFails: acc?.softFails + (c?.softFails || 0),
    ispRejections: acc?.ispRejections + (c?.ispRejections || 0),
    retryQueue: acc?.retryQueue + (c?.retryQueue || 0),
  }), { totalSent: 0, delivered: 0, bounced: 0, hardFails: 0, softFails: 0, ispRejections: 0, retryQueue: 0 }), [campaigns]);

  // Use the most recent campaign for drilldown (or aggregate across all if only one)
  const latestCampaign = campaigns?.[0];

  const openDrilldown = (metricKey, metricLabel) => {
    if (!latestCampaign) return;
    setDrilldown({
      metricKey,
      metricLabel,
      campaignId: latestCampaign?.id,
      campaignSubject: campaigns?.length === 1
        ? latestCampaign?.subject
        : `All Campaigns (showing latest: ${latestCampaign?.subject})`,
    });
  };

  const statusItems = [
    {
      key: 'delivered',
      label: 'Accepted by Resend',
      count: aggregate?.delivered,
      icon: CheckCircle2,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      barColor: 'bg-emerald-500',
      badgeColor: 'bg-emerald-100 text-emerald-700',
      description: 'Emails accepted into Resend\'s queue — does NOT confirm inbox delivery',
    },
    {
      key: 'bounced',
      label: 'Total Bounced',
      count: aggregate?.bounced,
      icon: XCircle,
      iconColor: 'text-rose-600',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      barColor: 'bg-rose-500',
      badgeColor: 'bg-rose-100 text-rose-700',
      description: 'All emails that failed delivery (hard + soft)',
    },
    {
      key: 'hardFails',
      label: 'Hard Fails',
      count: aggregate?.hardFails,
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      barColor: 'bg-red-500',
      badgeColor: 'bg-red-100 text-red-700',
      description: 'Permanent failures — invalid address, domain does not exist',
    },
    {
      key: 'softFails',
      label: 'Soft Fails',
      count: aggregate?.softFails,
      icon: Clock,
      iconColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      barColor: 'bg-amber-500',
      badgeColor: 'bg-amber-100 text-amber-700',
      description: 'Temporary failures — mailbox full, server unavailable',
    },
    {
      key: 'ispRejections',
      label: 'ISP Rejections',
      count: aggregate?.ispRejections,
      icon: ShieldAlert,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      barColor: 'bg-orange-500',
      badgeColor: 'bg-orange-100 text-orange-700',
      description: 'Blocked by ISP spam filters or domain reputation rules',
    },
    {
      key: 'retryQueue',
      label: 'Retry Queue',
      count: aggregate?.retryQueue,
      icon: RotateCcw,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      barColor: 'bg-blue-500',
      badgeColor: 'bg-blue-100 text-blue-700',
      description: 'Soft fails scheduled for automatic re-delivery',
    },
  ];

  const pieData = [
    { name: 'Delivered', value: aggregate?.delivered, color: '#10b981' },
    { name: 'Hard Fails', value: aggregate?.hardFails, color: '#ef4444' },
    { name: 'Soft Fails', value: aggregate?.softFails, color: '#f59e0b' },
    { name: 'ISP Rejected', value: aggregate?.ispRejections, color: '#f97316' },
    { name: 'Retry Queue', value: aggregate?.retryQueue, color: '#3b82f6' },
  ];

  return (
    <div className="space-y-4">
      {/* Drilldown modal */}
      {drilldown && (
        <MetricDrilldownModal
          isOpen={!!drilldown}
          onClose={() => setDrilldown(null)}
          metricKey={drilldown?.metricKey}
          metricLabel={drilldown?.metricLabel}
          campaignId={drilldown?.campaignId}
          campaignSubject={drilldown?.campaignSubject}
        />
      )}
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Send className="w-4 h-4 text-teal-600" />
        <h2 className="text-sm font-bold text-gray-800">Delivery Status Breakdown</h2>
        <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">All Campaigns Combined</span>
      </div>
      {/* Status cards grid — clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {statusItems?.map((item) => {
          const pctVal = pct(item?.count, aggregate?.totalSent);
          return (
            <button
              key={item?.key}
              onClick={() => openDrilldown(item?.key, item?.label)}
              className={`bg-white rounded-2xl border ${item?.borderColor} p-4 flex flex-col gap-2.5 hover:shadow-md hover:scale-[1.02] active:scale-[0.99] transition-all cursor-pointer text-left w-full group`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${item?.bgColor}`}>
                  <item.icon className={`w-4.5 h-4.5 ${item?.iconColor}`} style={{ width: 18, height: 18 }} />
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item?.badgeColor}`}>{pctVal}%</span>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{fmt(item?.count)}</p>
                <p className="text-xs font-semibold text-gray-700 mt-0.5">{item?.label}</p>
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">{item?.description}</p>
              </div>
              {/* Mini bar */}
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${item?.barColor} transition-all duration-700`}
                  style={{ width: `${Math.min(parseFloat(pctVal), 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 group-hover:text-teal-600 transition-colors font-medium">
                Click to view affected emails →
              </p>
            </button>
          );
        })}
      </div>
      {/* Pie chart + breakdown table */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Delivery Distribution</p>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Pie */}
          <div className="flex-shrink-0">
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [fmt(value), name]}
                  contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Legend + stats */}
          <div className="flex-1 w-full space-y-2">
            {pieData?.map((item) => (
              <div key={item?.name} className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item?.color }} />
                <span className="text-xs text-gray-600 flex-1">{item?.name}</span>
                <span className="text-xs font-bold text-gray-800">{fmt(item?.value)}</span>
                <span className="text-xs text-gray-400 w-12 text-right">{pct(item?.value, aggregate?.totalSent)}%</span>
              </div>
            ))}
            <div className="pt-2 border-t border-gray-100 flex items-center gap-3">
              <span className="w-3 h-3 rounded-full flex-shrink-0 bg-gray-300" />
              <span className="text-xs font-semibold text-gray-700 flex-1">Total Sent</span>
              <span className="text-xs font-bold text-gray-900">{fmt(aggregate?.totalSent)}</span>
              <span className="text-xs text-gray-400 w-12 text-right">100%</span>
            </div>
          </div>
        </div>
      </div>
      {/* Bounce type breakdown */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-rose-500" />
            <h3 className="text-sm font-bold text-gray-800">Bounce Type Analysis</h3>
          </div>
          <span className="text-xs text-gray-400">{fmt(aggregate?.bounced)} total bounces</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Campaign</th>
                <th className="text-right px-4 py-3 font-semibold">Hard Fails</th>
                <th className="text-right px-4 py-3 font-semibold">Soft Fails</th>
                <th className="text-right px-4 py-3 font-semibold">ISP Rejected</th>
                <th className="text-right px-4 py-3 font-semibold">Total Bounced</th>
                <th className="text-right px-4 py-3 font-semibold">Bounce Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns?.map(c => (
                <tr key={c?.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-gray-800 truncate max-w-[200px] text-xs">{c?.subject}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(c.sentAt)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => setDrilldown({ metricKey: 'hardFails', metricLabel: 'Hard Fails', campaignId: c?.id, campaignSubject: c?.subject })}
                      className="inline-flex items-center gap-1 text-red-600 font-semibold text-xs hover:underline"
                    >
                      <AlertTriangle className="w-3 h-3" />{fmt(c?.hardFails)}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => setDrilldown({ metricKey: 'softFails', metricLabel: 'Soft Fails', campaignId: c?.id, campaignSubject: c?.subject })}
                      className="inline-flex items-center gap-1 text-amber-600 font-semibold text-xs hover:underline"
                    >
                      <Clock className="w-3 h-3" />{fmt(c?.softFails)}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => setDrilldown({ metricKey: 'ispRejections', metricLabel: 'ISP Rejections', campaignId: c?.id, campaignSubject: c?.subject })}
                      className="inline-flex items-center gap-1 text-orange-600 font-semibold text-xs hover:underline"
                    >
                      <ShieldAlert className="w-3 h-3" />{fmt(c?.ispRejections)}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => setDrilldown({ metricKey: 'bounced', metricLabel: 'Total Bounced', campaignId: c?.id, campaignSubject: c?.subject })}
                      className="text-rose-600 font-bold text-xs hover:underline"
                    >
                      {fmt(c?.bounced)}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${parseFloat(pct(c?.bounced, c?.totalSent)) > 3 ? 'bg-rose-100 text-rose-700' : 'bg-green-100 text-green-700'}`}>
                      {pct(c?.bounced, c?.totalSent)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Retry Queue */}
      <div className="bg-white rounded-2xl border border-blue-200 overflow-hidden">
        <div
          className="px-5 py-4 border-b border-blue-100 flex items-center justify-between cursor-pointer hover:bg-blue-50 transition-colors"
          onClick={() => setActiveRetry(v => v === 'open' ? null : 'open')}
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center">
              <RotateCcw className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-800">Retry Queue</h3>
              <p className="text-xs text-gray-500">Soft fails awaiting automatic re-delivery</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">{fmt(aggregate?.retryQueue)} pending</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${activeRetry === 'open' ? 'rotate-180' : ''}`} />
          </div>
        </div>
        {activeRetry === 'open' && (
          <div>
            {/* Info banner */}
            <div className="mx-5 mt-4 mb-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-3">
              <RotateCcw className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                Soft-fail emails are automatically retried up to <strong>3 times</strong> over 24 hours. After 3 failed attempts, they are reclassified as hard fails and removed from future sends.
              </p>
            </div>
            <div className="overflow-x-auto pb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <th className="text-left px-5 py-3 font-semibold">Recipient</th>
                    <th className="text-left px-4 py-3 font-semibold">Failure Reason</th>
                    <th className="text-left px-4 py-3 font-semibold">Campaign</th>
                    <th className="text-center px-4 py-3 font-semibold">Attempts</th>
                    <th className="text-right px-5 py-3 font-semibold">Next Retry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {aggregate?.retryQueue === 0 ? (
                    <tr><td colSpan={5} className="text-xs text-gray-400 text-center py-4">No emails currently in the retry queue.</td></tr>
                  ) : (
                    <tr><td colSpan={5} className="px-5 py-3 text-xs text-gray-500">
                      <strong>{fmt(aggregate?.retryQueue)}</strong> soft-fail emails are queued for automatic re-delivery across all campaigns. Retry details are managed by Resend and will update after the next send cycle.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Empty state component ──────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
      <Mail className="w-8 h-8 text-teal-400" />
    </div>
    <h3 className="text-base font-bold text-gray-700 mb-1">No campaigns sent yet</h3>
    <p className="text-sm text-gray-400 max-w-xs">
      Once you send a re-engagement campaign from the Officers Dashboard, analytics will appear here automatically.
    </p>
  </div>
);

const EmailCampaignAnalytics = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('email_campaigns')?.select('*')?.order('sent_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Map snake_case DB columns to camelCase for the component
      const mapped = (data || [])?.map(c => ({
        id: c?.id,
        campaignRef: c?.campaign_ref,
        sentAt: c?.sent_at,
        subject: c?.subject,
        totalSent: c?.total_sent,
        delivered: c?.delivered,
        opened: c?.opened,
        clicked: c?.clicked,
        unsubscribed: c?.unsubscribed,
        bounced: c?.bounced,
        hardFails: c?.hard_fails,
        softFails: c?.soft_fails,
        ispRejections: c?.isp_rejections,
        retryQueue: c?.retry_queue,
        status: c?.status,
      }));

      setCampaigns(mapped);
      if (mapped?.length > 0) {
        setSelectedId(mapped?.[0]?.id);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const selected = useMemo(() => campaigns?.find(c => c?.id === selectedId) || campaigns?.[0], [selectedId, campaigns]);

  // Latest campaign (most recent send) — used as the primary "current campaign" metrics
  const latestCampaign = campaigns?.[0];

  // Aggregate metrics across all campaigns (historical total)
  const aggregate = useMemo(() => {
    return campaigns?.reduce((acc, c) => ({
      sent: acc?.sent + (c?.totalSent || 0),
      delivered: acc?.delivered + (c?.delivered || 0),
      opened: acc?.opened + (c?.opened || 0),
      clicked: acc?.clicked + (c?.clicked || 0),
      unsubscribed: acc?.unsubscribed + (c?.unsubscribed || 0),
    }), { sent: 0, delivered: 0, opened: 0, clicked: 0, unsubscribed: 0 });
  }, [campaigns]);

  // Trend chart data (all campaigns over time, oldest first)
  const trendData = useMemo(() =>
    [...campaigns]?.reverse()?.map(c => ({
      name: new Date(c.sentAt)?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
      'Open Rate': parseFloat(pct(c?.opened, c?.delivered)),
      'CTR': parseFloat(pct(c?.clicked, c?.delivered)),
    })),
    [campaigns]
  );

  // Per-campaign bar data
  const barData = useMemo(() =>
    [...campaigns]?.reverse()?.map(c => ({
      name: new Date(c.sentAt)?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
      Opened: c?.opened,
      Clicked: c?.clicked,
      Bounced: c?.bounced,
    })),
    [campaigns]
  );

  const openRate = pct(selected?.opened, selected?.delivered);
  const ctr = pct(selected?.clicked, selected?.delivered);
  const deliveryRate = pct(selected?.delivered, selected?.totalSent);
  const unsubRate = pct(selected?.unsubscribed, selected?.delivered);

  return (
    <>
      <Helmet>
        <title>Email Campaign Analytics — MyBodyToday Officers</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate('/my-body-today-officers-master-dashboard')}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900 leading-tight">Email Campaign Analytics</h1>
                <p className="text-xs text-gray-500">Re-engagement campaign performance</p>
              </div>
            </div>
            <button
              onClick={fetchCampaigns}
              disabled={loading}
              className="ml-auto w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6 pb-24">

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
                <p className="text-sm text-gray-500">Loading campaign analytics…</p>
              </div>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-700">Failed to load analytics</p>
                <p className="text-xs text-rose-500 mt-0.5">{error}</p>
                <button onClick={fetchCampaigns} className="mt-2 text-xs text-rose-600 underline">Try again</button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && campaigns?.length === 0 && <EmptyState />}

          {/* Data loaded */}
          {!loading && !error && campaigns?.length > 0 && (
            <>
              {/* ── Latest Campaign Metrics (primary view) ── */}
              {latestCampaign && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-teal-500" />
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Latest Campaign</h2>
                    <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                      {new Date(latestCampaign.sentAt)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MetricCard icon={Mail} label="Emails Sent" value={fmt(latestCampaign?.totalSent)} color="teal" sub="This campaign only" trend={undefined} />
                    <MetricCard icon={Eye} label="Accepted by Resend" value={fmt(latestCampaign?.delivered)} sub={`${pct(latestCampaign?.delivered, latestCampaign?.totalSent)}% acceptance rate`} color="blue" trend={undefined} />
                    <MetricCard icon={MousePointerClick} label="Total Clicked" value={fmt(latestCampaign?.clicked)} sub={`${pct(latestCampaign?.clicked, latestCampaign?.delivered)}% CTR`} color="violet" trend={undefined} />
                    <MetricCard icon={Users} label="Bounced" value={fmt(latestCampaign?.bounced)} sub={`${pct(latestCampaign?.bounced, latestCampaign?.totalSent)}% bounce rate`} color="rose" trend={undefined} />
                  </div>
                </div>
              )}

              {/* ── Delivery Status Breakdown (uses latest campaign for drilldown) ── */}
              <DeliveryStatusBreakdown campaigns={campaigns} />

              {/* ── All-time aggregate (historical, all attempts) ── */}
              {campaigns?.length > 1 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">All Campaigns — Historical Total</h2>
                    <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{campaigns?.length} sends</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <MetricCard icon={Mail} label="Total Sent (All)" value={fmt(aggregate?.sent)} color="teal" sub="Across all send attempts" trend={undefined} />
                    <MetricCard icon={Eye} label="Total Opened" value={fmt(aggregate?.opened)} sub={`${pct(aggregate?.opened, aggregate?.delivered)}% open rate`} color="blue" trend={undefined} />
                    <MetricCard icon={MousePointerClick} label="Total Clicked" value={fmt(aggregate?.clicked)} sub={`${pct(aggregate?.clicked, aggregate?.delivered)}% CTR`} color="violet" trend={undefined} />
                    <MetricCard icon={Users} label="Unsubscribed" value={fmt(aggregate?.unsubscribed)} sub={`${pct(aggregate?.unsubscribed, aggregate?.delivered)}% rate`} color="rose" trend={undefined} />
                  </div>
                </div>
              )}

              {/* ── Trend chart ── */}
              {campaigns?.length > 1 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp className="w-4 h-4 text-teal-600" />
                    <h2 className="text-sm font-bold text-gray-800">Open Rate & CTR Over Time</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} unit="%" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                      <Line type="monotone" dataKey="Open Rate" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 4, fill: '#0d9488' }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="CTR" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 4, fill: '#7c3aed' }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── Per-campaign bar chart ── */}
              {campaigns?.length > 1 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-5">
                    <BarChart2 className="w-4 h-4 text-teal-600" />
                    <h2 className="text-sm font-bold text-gray-800">Engagement per Campaign Send</h2>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={barData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                      <Bar dataKey="Opened" fill="#0d9488" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Clicked" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Bounced" fill="#f87171" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ── Campaign selector + detail ── */}
              {selected && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Campaign Detail</h2>
                    {/* Dropdown */}
                    {campaigns?.length > 1 && (
                      <div className="relative">
                        <button
                          onClick={() => setDropdownOpen(v => !v)}
                          className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 font-medium hover:border-teal-300 transition-colors"
                        >
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {new Date(selected.sentAt)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} at {new Date(selected.sentAt)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        {dropdownOpen && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 min-w-[260px] overflow-hidden">
                            {campaigns?.map(c => (
                              <button
                                key={c?.id}
                                onClick={() => { setSelectedId(c?.id); setDropdownOpen(false); }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${c?.id === selectedId ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-gray-700'}`}
                              >
                                <p className="font-medium truncate">{c?.subject}</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {new Date(c.sentAt)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · {fmt(c?.totalSent)} recipients
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Detail card */}
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-4">
                      <p className="text-white font-bold text-sm leading-snug">{selected?.subject}</p>
                      <p className="text-teal-100 text-xs mt-1">
                        Sent {new Date(selected.sentAt)?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} at {new Date(selected.sentAt)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>

                    {/* Metric grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y divide-gray-100">
                      {[
                        { label: 'Acceptance Rate', value: `${deliveryRate}%`, sub: `${fmt(selected?.delivered)} / ${fmt(selected?.totalSent)}`, color: 'text-teal-600' },
                        { label: 'Open Rate', value: `${openRate}%`, sub: `${fmt(selected?.opened)} opened`, color: 'text-blue-600' },
                        { label: 'Click-Through Rate', value: `${ctr}%`, sub: `${fmt(selected?.clicked)} clicked`, color: 'text-violet-600' },
                        { label: 'Unsubscribe Rate', value: `${unsubRate}%`, sub: `${fmt(selected?.unsubscribed)} unsubscribed`, color: 'text-rose-500' },
                      ]?.map((m, i) => (
                        <div key={i} className="p-4 flex flex-col gap-1">
                          <p className={`text-2xl font-bold ${m?.color}`}>{m?.value}</p>
                          <p className="text-xs font-semibold text-gray-700">{m?.label}</p>
                          <p className="text-xs text-gray-400">{m?.sub}</p>
                        </div>
                      ))}
                    </div>

                    {/* Engagement funnel */}
                    <div className="px-5 py-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Engagement Funnel</p>
                      <div className="space-y-2.5">
                        {[
                          { label: 'Sent', count: selected?.totalSent, pctVal: 100, color: 'bg-gray-300' },
                          { label: 'Accepted', count: selected?.delivered, pctVal: parseFloat(deliveryRate), color: 'bg-teal-400' },
                          { label: 'Opened', count: selected?.opened, pctVal: parseFloat(openRate), color: 'bg-blue-500' },
                          { label: 'Clicked', count: selected?.clicked, pctVal: parseFloat(ctr), color: 'bg-violet-500' },
                        ]?.map((row, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-20 flex-shrink-0">{row?.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${row?.color} transition-all duration-700`}
                                style={{ width: `${Math.min(parseFloat(row?.pctVal), 100)}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-gray-700 w-16 text-right">{fmt(row?.count)} <span className="text-gray-400 font-normal">({row?.pctVal}%)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Per-campaign delivery status */}
                    <div className="px-5 py-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Delivery Status — This Campaign</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { label: 'Hard Fails', count: selected?.hardFails, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100' },
                          { label: 'Soft Fails', count: selected?.softFails, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
                          { label: 'ISP Rejections', count: selected?.ispRejections, icon: ShieldAlert, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100' },
                          { label: 'Retry Queue', count: selected?.retryQueue, icon: RotateCcw, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
                          { label: 'Total Bounced', count: selected?.bounced, icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100' },
                          { label: 'Bounce Rate', count: `${pct(selected?.bounced, selected?.totalSent)}%`, icon: TrendingDown, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
                        ]?.map((item, i) => (
                          <div key={i} className={`flex items-center gap-3 rounded-xl border ${item?.border} ${item?.bg} px-3 py-2.5`}>
                            <item.icon className={`w-4 h-4 ${item?.color} flex-shrink-0`} />
                            <div>
                              <p className={`text-sm font-bold ${item?.color}`}>{typeof item?.count === 'number' ? fmt(item?.count) : item?.count}</p>
                              <p className="text-xs text-gray-500">{item?.label}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── All campaigns table ── */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-teal-600" />
                    <h2 className="text-sm font-bold text-gray-800">All Campaigns</h2>
                  </div>
                  <span className="text-xs text-gray-400">{campaigns?.length} campaign{campaigns?.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="text-left px-5 py-3 font-semibold">Campaign</th>
                        <th className="text-right px-4 py-3 font-semibold">Sent</th>
                        <th className="text-right px-4 py-3 font-semibold">Accepted</th>
                        <th className="text-right px-4 py-3 font-semibold">CTR</th>
                        <th className="text-right px-4 py-3 font-semibold">Bounced</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {campaigns?.map((c, idx) => (
                        <tr
                          key={c?.id}
                          onClick={() => setSelectedId(c?.id)}
                          className={`cursor-pointer hover:bg-gray-50 transition-colors ${c?.id === selectedId ? 'bg-teal-50' : ''}`}
                        >
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              {idx === 0 && (
                                <span className="text-xs font-semibold bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-md flex-shrink-0">Latest</span>
                              )}
                              <div>
                                <p className="font-medium text-gray-800 truncate max-w-[200px] text-xs">{c?.subject}</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {new Date(c.sentAt)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-right text-gray-700 font-medium">{fmt(c?.totalSent)}</td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-teal-600 font-semibold">{fmt(c?.delivered)}</span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-violet-600 font-semibold">{pct(c?.clicked, c?.delivered)}%</span>
                          </td>
                          <td className="px-4 py-3.5 text-right text-rose-500 font-medium">{fmt(c?.bounced)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default EmailCampaignAnalytics;
